// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useEffect, useMemo, useState } from 'react';
import { get } from '@tls/auth';
import styled from 'styled-components';
import { blobToBase64 } from 'components/treactui-template/meta/loadImageIntoElement';
import NoContentBackdrop from 'components/treactui-template/organisms/backdrop/NoContentBackdrop';
import BackdropLoading from 'components/treactui-template/organisms/backdrop/loading/BackdropLoading';
import useToolHelpers from 'hooks/useToolHelpers';
import '../../../common/webcomponents.d.ts';
import useProjectsMetadata from 'hooks/useProjectsMetadata.js';
import CanvasZoom from '../../label/canvas/CanvasZoom.js';
import { useParams } from 'react-router-dom';
import ParamsWithId from 'model/ParamsWithId.js';
import { getColorOption, IdToColor } from 'components/common/ColorOptions.js';

export type Props = {
  projectId?: string;
  imageId?: string;
  hasMask?: boolean;
};

type MaskResult = {
  data?: Blob;
  status: number;
  statusText: string;
};

function DataDetailImage({ imageId, hasMask }: Readonly<Props>) {
  const [loading, setLoading] = useState(true);
  const [imageExist, setImageExist] = useState(true);
  const { setStepFromBlob } = useToolHelpers();
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);

  const modelClassesWithoutBackground = useMemo(
    () => selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0),
    [selectedProject?.projectUseCase?.modelClasses]
  );
  const colorOptions = useMemo(() => {
    return modelClassesWithoutBackground?.map(modelClass => getColorOption(modelClass.labelColor)) ?? [];
  }, [modelClassesWithoutBackground]);

  const labelsArray = useMemo(() => colorOptions.map(color => IdToColor(color.id)), [colorOptions]);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent('labelschanged', { bubbles: true, detail: { labels: labelsArray } }));
  }, [labelsArray]);

  useEffect(() => {
    if (!id || !imageId) {
      setImageExist(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    async function fetch() {
      setLoading(true);
      // We need to fetch both image and mask, otherwise the label will be erased.
      const imageGet = get(`projects/${id}/labels/${imageId}/image`, false, false, {
        responseType: 'blob',
        signal: controller.signal,
      });

      let maskGet: Promise<MaskResult> | undefined = undefined;
      if (hasMask) {
        maskGet = get(`projects/${id}/labels/${imageId}/mask`, false, false, {
          responseType: 'blob',
          signal: controller.signal,
        });
      }

      const imageResult = await imageGet;
      if (controller.signal.aborted) return;
      let maskResult: MaskResult | undefined = undefined;
      if (maskGet) {
        maskResult = await maskGet;
        if (controller.signal.aborted) return;
      }

      if (!imageResult.data || imageResult.status < 200 || imageResult.status > 299) {
        setImageExist(false);
        setLoading(false);
        return;
      }
      setImageExist(true);
      let base64 = await blobToBase64(imageResult.data);
      if (controller.signal.aborted) return;
      setStepFromBlob(imageResult.data);

      document.body.dispatchEvent(
        new CustomEvent('image-selected', {
          detail: {
            imageId,
            src: base64,
            title: `Image - ${imageId}`,
          },
          bubbles: true,
        })
      );

      if (!maskResult?.data || maskResult.status < 200 || maskResult.status > 299) {
        setLoading(false);
        return;
      }
      base64 = await blobToBase64(maskResult.data);
      if (controller.signal.aborted) return;

      document.body.dispatchEvent(
        new CustomEvent('maskimported', {
          detail: {
            imageData: base64,
            imageId,
            title: 'Image',
          },
          bubbles: true,
        })
      );

      setLoading(false);
    }

    fetch();
    return () => controller.abort();
  }, [id, imageId, selectedProject?.projectUseCase?.modelClasses, hasMask, setStepFromBlob]);

  const canvas = useMemo(
    () => (
      <canvas-image-layer data-testid='data-detail-canvas-image-layer'>
        <mask-component></mask-component>
      </canvas-image-layer>
    ),
    []
  );

  return (
    <ImageContent>
      {canvas}
      <NoContentBackdrop show={!loading && !imageExist} />
      <BackdropLoading loading={loading} label='' />
      <CanvasZoom />
    </ImageContent>
  );
}
const ImageContent = styled.div`
  margin: 1rem;
  width: calc(100% - 2rem);
  height: calc(100% - 2rem);
  position: relative;
`;

export default DataDetailImage;
